import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useIsAuthenticated } from '@azure/msal-react';
import { useMe, useUpdateSettings, useUpdateBusinessName } from '../api/hooks';
import { RowGroup, RowField } from '../components/RowField';
import { usePageTitle } from '../hooks/usePageTitle';
import type { Settings } from '../api/types';

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, Number.isFinite(n) ? n : lo));

export function SettingsPage() {
  usePageTitle('Settings');
  const isAuthenticated = useIsAuthenticated();
  const { data: me } = useMe(isAuthenticated);
  const updateSettings = useUpdateSettings();
  const updateBusinessName = useUpdateBusinessName();

  if (!isAuthenticated) return <div className="mx-auto max-w-2xl px-4 py-16 text-slate-500">Sign in to manage settings.</div>;
  if (!me) return <div className="mx-auto max-w-2xl px-4 py-8 text-slate-500">Loading…</div>;

  const s = me.settings;
  const patch = (p: Partial<Settings>) => updateSettings.mutate(p);

  return (
    <div className="mx-auto max-w-2xl space-y-5 px-4 py-6 pb-24 sm:pb-8">
      <h1 className="font-display text-2xl font-semibold text-ink">Settings</h1>

      <RowGroup title="Business">
        <RowField type="text" label="Business name" value={me.businessName} onCommit={(v) => v.trim() && updateBusinessName.mutate(v.trim())} />
      </RowGroup>

      <RowGroup title="Contact (shown on documents)">
        <RowField type="text" label="Contact name" value={s.contactName ?? ''} placeholder="Add" onCommit={(v) => patch({ contactName: v })} />
        <RowField type="text" label="Contact phone" value={s.contactPhone ?? ''} placeholder="Add" onCommit={(v) => patch({ contactPhone: v })} />
        <RowField type="text" label="Contact email" value={s.contactEmail ?? ''} placeholder="Add" onCommit={(v) => patch({ contactEmail: v })} />
      </RowGroup>

      <RowGroup title="Money & numbering">
        <RowField type="text" label="Per-mile rate (USD)" value={String(s.perMileRate)} onCommit={(v) => patch({ perMileRate: Math.max(0, Number(v) || 0) })} />
        <RowField type="text" label="Default currency" value={s.defaultCurrency} onCommit={(v) => patch({ defaultCurrency: v.toUpperCase().slice(0, 3) })} />
        <RowField type="text" label="Receipt prefix" value={s.receiptPrefix} onCommit={(v) => patch({ receiptPrefix: v.slice(0, 10) })} />
      </RowGroup>

      <RowGroup title="Document validity">
        <RowField type="text" label="Grace period after expiry (days)" value={String(s.poaContingencyBufferDays)} onCommit={(v) => patch({ poaContingencyBufferDays: clamp(Number(v), 0, 365) })} />
        <RowField type="text" label="Signed document validity (days)" value={String(s.nonVisitDocValidityDays)} onCommit={(v) => patch({ nonVisitDocValidityDays: clamp(Number(v), 1, 3650) })} />
      </RowGroup>

      <RowGroup title="Scheduling">
        <RowField type="toggle" label="Prevent overlapping visits" value={s.overlapValidationEnabled} onChange={(v) => patch({ overlapValidationEnabled: v })} />
      </RowGroup>

      <RowGroup title="Setup">
        <NavRow to="/settings/calendar-feed" label="Calendar subscription" desc="Subscribe to your visits in Google or Apple Calendar." />
        <NavRow to="/settings/records" label="Client records" desc="Configure the per-client record type and its fields." />
        <NavRow to="/settings/document-kinds" label="Document kinds" desc="Manage the categories you can assign to templates." />
      </RowGroup>
    </div>
  );
}

function NavRow({ to, label, desc }: { to: string; label: string; desc: string }) {
  return (
    <Link to={to} className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-slate-50">
      <span className="min-w-0">
        <span className="block text-sm font-medium text-ink">{label}</span>
        <span className="mt-0.5 block text-xs text-slate-500">{desc}</span>
      </span>
      <ChevronRight size={16} className="shrink-0 text-slate-400" />
    </Link>
  );
}
